import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Target, ShieldCheck, Clock } from "lucide-react";

const items = [
  {
    icon: Target,
    title: "Selective deal volume",
    text: "We invest in a limited number of companies to maintain disciplined underwriting and meaningful engagement.",
  },
  {
    icon: ShieldCheck,
    title: "Regulated market focus",
    text: "We target businesses operating in regulated and infrastructure-heavy markets where barriers to entry support long-term value.",
  },
  {
    icon: Clock,
    title: "Multi-year capital horizon",
    text: "We approach capital deployment with a long-term perspective and participate selectively in follow-on rounds.",
  },
];

export default function StructuredCapital() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      data-testid="structured-capital-section"
      className="py-[30px] md:py-[75px]"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.97 }}
          animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="structured-bg wave-lines relative rounded-[2.5rem] overflow-hidden"
        >
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-[38px] lg:gap-16 p-10 md:p-14 lg:p-16">
            {/* Left side */}
            <div className="flex flex-col justify-center">
              <h2
                data-testid="structured-capital-heading"
                className="font-body text-[30px] sm:text-[45px] tracking-tight leading-[1.1] font-semibold text-white mb-[29px]"
              >
                Structured capital.<br />
                Concentrated conviction.
              </h2>
              <p className="font-body text-base md:text-lg leading-relaxed text-indigo-100/80 max-w-lg mb-1 md:mb-8">
                Our model is built on selectivity, structural underwriting, and long-term alignment. We prioritize depth over volume and deploy capital where we see sustained execution and durable market positioning.
              </p>
            </div>

            {/* Right side - 3 items */}
            <div className="flex flex-col gap-8 justify-center">
              {items.map((item, i) => (
                <motion.div
                  key={item.title}
                  data-testid={`structured-item-${i + 1}`}
                  initial={{ opacity: 0, x: 40, y: 10 }}
                  animate={isInView ? { opacity: 1, x: 0, y: 0 } : {}}
                  transition={{ duration: 0.65, delay: 0.35 + i * 0.18, ease: [0.22, 1, 0.36, 1] }}
                  className="group"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/10">
                      <item.icon className="w-5 h-5 text-indigo-200" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-body text-lg font-semibold text-white mb-1.5 tracking-tight">
                        {item.title}
                      </h3>
                      <p className="font-body text-sm leading-relaxed text-indigo-200/70">
                        {item.text}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
